import { Inject, Injectable } from '@angular/core';
import { SessionController } from 'src/app/auth/session/session-controller';
import { ACTIVE_STATES, SessionState } from 'src/app/auth/session/session-state-types';
import { CancellationTokenHandler } from 'src/app/common/cancellation/cancellationtoken-handler';
import { ICancellationTokenHandler } from 'src/app/common/contracts/cancellation/cancellationtoken-handler';
import { Deferred } from 'src/app/common/deferred/deferred';

import makeDebug from '../../../../makeDebug';
import { IScheduler } from '../contracts/scheduler/scheduler';
import { IPipelineContext } from '../contracts/start/pipeline-context';
import { ISyncRegistry } from '../contracts/sync/sync-registry';

const debug = makeDebug('sync:sync-trigger');

export interface ManualRunOptions {
  showSyncIndicator?: boolean;
}

/**
 * Starts the sync pipeline. Nothing else does.
 *
 * Two inputs, one job. During app start the `SyncRegistry` — the last element of the start
 * pipeline — registers itself here via `schedule()` together with the context it wants to be run
 * with. From then on this class starts that registry whenever the session changes, and once more
 * when the user taps "synchronize" on the dashboard (`runManual()`).
 *
 * The session state is the only thing that decides whether and how a run happens:
 *
 * | session state | what happens                                                              |
 * |---------------|---------------------------------------------------------------------------|
 * | `starting`    | nothing — the token and the cache have not been read yet                  |
 * | `loggedOut`   | the running work is cancelled; no run — there is no account to sync for   |
 * | `online`      | a synced run: `isOnline: true`, the elements talk to the backend          |
 * | `offline`     | the running work is cancelled, then a local run: `isOnline: false`        |
 * | `degraded`    | same as `offline`                                                         |
 *
 * `isOnline` is read from the session at the moment the run starts and written into the run's
 * context. The elements never ask whether the user is authenticated: a run exists only because
 * the session said so, and `online` is entered only after the socket was authenticated with a
 * checked token (see `SessionController`).
 *
 * Scenarios this covers:
 * - Cold start with backend: `starting → online`, one synced run with the startup indicator.
 * - Cold start without backend but with a cached account: `starting → offline`, one local run;
 *   the databases open, the command queue stays. When the socket comes: `offline → online`,
 *   a synced run.
 * - Socket lost while working: `online → offline` cancels the data sync mid-flight and runs
 *   locally; the reconnect runs synced again.
 * - Maintenance window: identical to a lost socket, the controller maps both to `offline`.
 * - Backend or identity provider flaky (`degraded`): local run now, the controller's retries end
 *   in `online` and with it the synced run — no polling in here.
 * - Sign-in: the login page starts no run; the transition to `online` is the run.
 * - Logout: cancel, nothing else; the next run comes with the next login.
 * - App resumed from background: no handling here. The controller re-evaluates on resume, and if
 *   that changes the state, the change is the run.
 * - Manual sync on the dashboard: like a state-driven run, with `showSyncIndicator` passed
 *   through; refused when there is no session.
 *
 * Runs that overlap are the `SyncRegistry`'s problem: it queues at most one pending run and
 * starts it when the current one finishes.
 */
@Injectable({ providedIn: 'root' })
export class SyncTrigger implements IScheduler {
  private readonly _target = new Deferred<{ syncRegistry: ISyncRegistry; context: IPipelineContext }>();

  constructor(
    private readonly _session: SessionController,
    @Inject(CancellationTokenHandler) private readonly _cancellationTokenHandler: ICancellationTokenHandler
  ) {
    this._session.state$.subscribe(state => void this.onSessionState(state));
  }

  public schedule(syncRegistry: ISyncRegistry, context: IPipelineContext): void {
    debug('pipeline registered');
    this._target.resolve({ syncRegistry, context });
  }

  /** A run requested by the user; `isOnline` comes from the session. Nothing runs without a session. */
  public async runManual(options: ManualRunOptions = {}): Promise<void> {
    if (!ACTIVE_STATES.has(this._session.state)) {
      debug(`manual run ignored in session state ${this._session.state}`);
      return;
    }
    await this.run('manual', options);
  }

  private async onSessionState(state: SessionState): Promise<void> {
    if (state === 'starting') {
      return;
    }
    if (state !== 'online') {
      debug(`session ${state}: cancelling running work`);
      await this._cancellationTokenHandler.cancellAll();
    }
    if (state === 'loggedOut') {
      return;
    }
    await this.run(`session ${state}`);
  }

  private async run(source: string, options: ManualRunOptions = {}): Promise<void> {
    const { syncRegistry, context } = await this._target.promise;
    const isOnline = this._session.state === 'online';
    window.logger.addBreadcrumb({ message: `[SyncTrigger] run | source=${source} online=${isOnline}` });
    debug('starting pipeline', source, isOnline);
    try {
      await syncRegistry.start({
        ...context,
        id: `${Date.now()}`,
        params: { isOnline, showSyncIndicator: options.showSyncIndicator },
      });
    } catch (error) {
      window.logger.error('[SyncTrigger] pipeline run failed', error);
    }
  }
}
